import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Tag, Percent, DollarSign, Calendar, Users, ShieldAlert, CheckCircle2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { couponService } from '../../../services/couponService';
import { sellerProductService } from '../../../services/sellerProductService';
import { componentService } from '../../../services/componentService';
import toast from 'react-hot-toast';
import { formatPrice } from '../../../utils/formatters';
import TierUpgradeModal from './TierUpgradeModal';

export default function CouponManagement({ sellerAccount, setActiveTab, onAccountUpdate }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingTaxRate] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isCreatorTier = sellerAccount?.tier === 'entry';

  useEffect(() => {
    if (!isCreatorTier && sellerAccount?.id) {
      loadCoupons();
    } else {
      setLoading(false);
    }
  }, [sellerAccount?.id, isCreatorTier]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await couponService.getMyCoupons();
      setCoupons(data || []);
    } catch (err) {
      console.error('Failed to load coupons:', err);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await couponService.delete(id);
      toast.success('Coupon deleted');
      loadCoupons();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleStatus = async (coupon) => {
    try {
      await couponService.update(coupon.id, { ...coupon, isActive: !coupon.isActive });
      toast.success(`Coupon ${coupon.isActive ? 'deactivated' : 'activated'}`);
      loadCoupons();
    } catch (err) {
      toast.error('Failed to update coupon status');
    }
  };

  if (isCreatorTier) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-border p-8 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Tag size={32} />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">Coupons Feature</h2>
          <p className="text-muted-foreground mb-8">
            The Couponing System is available for Creator Plus and Creator Professional plans. 
            Offer discounts to your customers and drive more sales!
          </p>
          <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Premium Marketing Tools:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-foreground/80">
                <CheckCircle2 size={16} className="text-green-500" /> Create custom discount codes
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground/80">
                <CheckCircle2 size={16} className="text-green-500" /> Fixed amount or percentage discounts
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground/80">
                <CheckCircle2 size={16} className="text-green-500" /> Limit coupons to specific products or components
              </li>
            </ul>
          </div>
          <Button onClick={() => setShowUpgradeModal(true)}>
            Upgrade Your Plan
          </Button>
        </div>
        
        {showUpgradeModal && (
          <TierUpgradeModal
            currentTier={sellerAccount.tier}
            accountId={sellerAccount.id}
            onClose={() => setShowUpgradeModal(false)}
            onUpgradeSuccess={(newTier) => {
              if (onAccountUpdate) onAccountUpdate();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-border overflow-hidden">
        <div className="p-6 border-b border-border flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Tag size={20} className="text-primary" />
              Coupon Management
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Manage discount codes for your store.</p>
          </div>
          <Button onClick={() => { setEditingTaxRate(null); setShowModal(true); }} className="flex items-center gap-2">
            <Plus size={16} /> Create Coupon
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-muted/30 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Discount</th>
                <th className="px-6 py-4">Applies To</th>
                <th className="px-6 py-4">Usage</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </td>
                </tr>
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground italic">
                    No coupons created yet.
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-muted/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-mono font-bold text-primary bg-primary/5 px-2 py-1 rounded inline-block">
                        {coupon.code}
                      </div>
                      {coupon.description && <p className="text-xs text-muted-foreground mt-1">{coupon.description}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 font-semibold">
                        {coupon.discountType === 'percentage' ? (
                          <><Percent size={14} /> {coupon.discountValue}%</>
                        ) : (
                          <>{formatPrice(coupon.discountValue)}</>
                        )}
                      </div>
                      {coupon.minPurchaseAmount > 0 && (
                        <p className="text-[10px] text-muted-foreground">Min: {formatPrice(coupon.minPurchaseAmount)}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs capitalize px-2 py-1 bg-muted rounded-full">
                        {coupon.appliesTo.replace('_', ' ')}
                      </span>
                      {coupon.applicableItemIds?.length > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-1">{coupon.applicableItemIds.length} items</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {coupon.usedCount} / {coupon.usageLimit || '∞'}
                    </td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => toggleStatus(coupon)}
                        className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => { setEditingTaxRate(coupon); setShowModal(true); }} className="p-2 text-muted-foreground hover:text-primary transition-colors">
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(coupon.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <CouponModal 
          isOpen={showModal} 
          onClose={() => setShowModal(false)} 
          onSave={loadCoupons}
          coupon={editingCoupon}
          sellerAccount={sellerAccount}
        />
      )}
    </div>
  );
}

function CouponModal({ isOpen, onClose, onSave, coupon, sellerAccount }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    code: coupon?.code || '',
    description: coupon?.description || '',
    discountType: coupon?.discountType || 'percentage',
    discountValue: coupon?.discountValue || 0,
    appliesTo: coupon?.appliesTo || 'all',
    applicableItemIds: coupon?.applicableItemIds || [],
    minPurchaseAmount: coupon?.minPurchaseAmount || 0,
    maxDiscountAmount: coupon?.maxDiscountAmount || '',
    startDate: coupon?.startDate ? new Date(coupon.startDate).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
    endDate: coupon?.endDate ? new Date(coupon.endDate).toISOString().slice(0, 16) : '',
    usageLimit: coupon?.usageLimit || '',
    isActive: coupon?.isActive !== undefined ? coupon.isActive : true,
    sellerId: sellerAccount.id
  });

  useEffect(() => {
    if (formData.appliesTo !== 'all') {
      fetchItems();
    }
  }, [formData.appliesTo]);

  const fetchItems = async () => {
    try {
      if (formData.appliesTo === 'specific_products') {
        const data = await sellerProductService.getSellerProductsBySellerId(sellerAccount.id);
        // Map to standard { id, name } structure
        setItems(data?.map(p => ({ id: p.productId, name: p.productName })) || []);
      } else if (formData.appliesTo === 'specific_components') {
        const data = await componentService.getAll({ sellerId: sellerAccount.id });
        setItems(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch items:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        minPurchaseAmount: parseFloat(formData.minPurchaseAmount),
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        endDate: formData.endDate || null
      };

      if (coupon) {
        await couponService.update(coupon.id, payload);
        toast.success('Coupon updated');
      } else {
        await couponService.create(payload);
        toast.success('Coupon created');
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save coupon:', err);
      toast.error(err.message || 'Failed to save coupon');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (id) => {
    const ids = formData.applicableItemIds || [];
    if (ids.includes(id)) {
      setFormData(prev => ({ ...prev, applicableItemIds: ids.filter(i => i !== id) }));
    } else {
      const newIds = [...ids, id];
      if (newIds.length === items.length && items.length > 0) {
        alert("You have selected all items. We will set this coupon to apply to your 'Entire Store'.");
        setFormData(prev => ({ ...prev, appliesTo: 'all', applicableItemIds: [] }));
      } else {
        setFormData(prev => ({ ...prev, applicableItemIds: newIds }));
      }
    }
  };

  const handleSelectAll = () => {
    if (formData.applicableItemIds.length === items.length && items.length > 0) {
      setFormData(prev => ({ ...prev, applicableItemIds: [] }));
    } else {
      alert("You are selecting all items. We will set this coupon to apply to your 'Entire Store'.");
      setFormData(prev => ({ ...prev, appliesTo: 'all', applicableItemIds: [] }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-xl font-bold">{coupon ? 'Edit Coupon' : 'Create New Coupon'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-bold">Coupon Code*</label>
              <input 
                required
                className="w-full border rounded-lg p-2 font-mono uppercase"
                placeholder="SUMMER2026"
                value={formData.code}
                onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-bold">Discount Type</label>
              <select 
                className="w-full border rounded-lg p-2"
                value={formData.discountType}
                onChange={e => setFormData({...formData, discountType: e.target.value})}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed_amount">Fixed Amount ($)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-bold">Discount Value*</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {formData.discountType === 'percentage' ? '%' : '$'}
                </span>
                <input 
                  required
                  type="number"
                  step="0.01"
                  className="w-full border rounded-lg p-2 pl-8"
                  value={formData.discountValue}
                  onChange={e => setFormData({...formData, discountValue: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-bold">Usage Limit</label>
              <input 
                type="number"
                className="w-full border rounded-lg p-2"
                placeholder="Unlimited"
                value={formData.usageLimit}
                onChange={e => setFormData({...formData, usageLimit: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-bold">Applies To</label>
            <select 
              className="w-full border rounded-lg p-2"
              value={formData.appliesTo}
              onChange={e => setFormData({...formData, appliesTo: e.target.value, applicableItemIds: []})}
            >
              <option value="all">Entire Store</option>
              <option value="specific_products">Specific Products</option>
              <option value="specific_components">Specific Components</option>
            </select>
          </div>

          {formData.appliesTo !== 'all' && (
            <div className="space-y-2 border rounded-lg p-4 bg-muted/20">
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold">Select {formData.appliesTo === 'specific_products' ? 'Products' : 'Components'}</label>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-primary hover:underline font-semibold"
                >
                  {formData.applicableItemIds.length === items.length && items.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                {items.map(item => (
                  <label key={item.id} className="flex items-center gap-2 p-2 hover:bg-white rounded border border-transparent hover:border-border cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.applicableItemIds.includes(item.id)}
                      onChange={() => toggleItem(item.id)}
                      className="rounded text-primary"
                    />
                    <span className="text-sm">{item.name || item.customName}</span>
                  </label>
                ))}
                {items.length === 0 && <p className="text-xs text-muted-foreground">No items found.</p>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-bold text-gray-700">Start Date</label>
              <input 
                type="datetime-local"
                className="w-full border rounded-lg p-2 text-sm"
                value={formData.startDate}
                onChange={e => setFormData({...formData, startDate: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-bold text-gray-700">End Date</label>
              <input 
                type="datetime-local"
                className="w-full border rounded-lg p-2 text-sm"
                value={formData.endDate}
                onChange={e => setFormData({...formData, endDate: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-bold">Minimum Purchase Amount ($)</label>
            <input 
              type="number"
              step="0.01"
              className="w-full border rounded-lg p-2"
              value={formData.minPurchaseAmount}
              onChange={e => setFormData({...formData, minPurchaseAmount: e.target.value})}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-bold">Description (Optional)</label>
            <textarea 
              className="w-full border rounded-lg p-2"
              rows="2"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
            />
          </div>
        </form>

        <div className="p-6 border-t border-border flex justify-end gap-3 bg-muted/10 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>{loading ? <Loader2 size={16} className="animate-spin" /> : 'Save Coupon'}</Button>
        </div>
      </div>
    </div>
  );
}

function X({ size }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
}

function Loader2({ size, className }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>;
}
